import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SimpleDataCacheService } from '@/lib/utils/simple-data-cache-service';
import { sortPlaces, getPlaceByName as getPlaceByNameWithFuzzyMatch } from './places-data-service';

interface PlacesDataContextType {
  places: Place[];
  getPlaceById: (id: string) => Place | undefined;
  getPlaceByName: (placeName: string) => Place | undefined;
}

export const PlacesDataContext = createContext<PlacesDataContextType | undefined>(undefined);

export function usePlacesData() {
  const context = useContext(PlacesDataContext);
  if (!context) {
    throw new Error('usePlacesData must be used within PlacesDataProvider');
  }
  return context;
}

interface PlacesDataProviderProps {
  children: ReactNode;
  places: Place[];
}

export function PlacesDataProvider({ children, places: initialData }: PlacesDataProviderProps) {
  const [cacheService] = useState(() =>
    new SimpleDataCacheService<Place>(
      initialData,
      sortPlaces
    )
  );

  // Build lookup maps for O(1) performance on common cases
  const [lookupMaps, setLookupMaps] = useState<{
    byId: Map<string, Place>;
    byName: Map<string, Place>;
    byAlternateName: Map<string, Place>;
  }>(() => {
    const byId = new Map<string, Place>();
    const byName = new Map<string, Place>();
    const byAlternateName = new Map<string, Place>();

    initialData.forEach(place => {
      if (place.id) {
        byId.set(place.id, place);
      }
      if (place.placeName) {
        byName.set(place.placeName.toLowerCase(), place);
      }
      place.alternateNames?.forEach(altName => {
        if (altName) {
          byAlternateName.set(altName.toLowerCase(), place);
        }
      });
    });

    return { byId, byName, byAlternateName };
  });

  useEffect(() => {
    cacheService.setNewItems(initialData);

    // Rebuild lookup maps when data changes
    const byId = new Map<string, Place>();
    const byName = new Map<string, Place>();
    const byAlternateName = new Map<string, Place>();

    initialData.forEach(place => {
      if (place.id) {
        byId.set(place.id, place);
      }
      if (place.placeName) {
        byName.set(place.placeName.toLowerCase(), place);
      }
      place.alternateNames?.forEach(altName => {
        if (altName) {
          byAlternateName.set(altName.toLowerCase(), place);
        }
      });
    });

    setLookupMaps({ byId, byName, byAlternateName });
  }, [initialData, cacheService]);

  const getPlaceById = (id: string): Place | undefined => {
    // Use O(1) map lookup instead of O(n) find
    return lookupMaps.byId.get(id);
  };

  const getPlaceByName = (placeName: string): Place | undefined => {
    if (!placeName) return undefined;

    const lowerName = placeName.toLowerCase();

    // 1. Try exact match (O(1))
    let match = lookupMaps.byName.get(lowerName);
    if (match) return match;

    // 2. Try alternate names (O(1))
    match = lookupMaps.byAlternateName.get(lowerName);
    if (match) return match;

    // 3. Fall back to fuzzy matching for edge cases (O(n))
    // This handles name variations like "Antioch (on the Orontes)" vs "Antioch on the Orontes"
    return getPlaceByNameWithFuzzyMatch(cacheService.getItems(), placeName);
  };

  return (
    <PlacesDataContext.Provider
      value={{
        places: cacheService.getItems(),
        getPlaceById,
        getPlaceByName
      }}
    >
      {children}
    </PlacesDataContext.Provider>
  );
}

